/**
 * When adding/removing pages, ensure this entry is updated. To preserve
 * references, the withDynamicImport HOC must be called with "name" as key, like
 * withDynamicImport(DYNAMIC_IMPORTS.CAMPAIGN_INDEX_PAGE.name). This way, the
 * reference of the component can be tracked from the component, to the entry in
 * this map, then to places where withDynamicImport is called.
 *
 * Do not use withDynamicImport('CampaignIndexPage'), since string literals
 * reference cannot be tracked
 *
 * When adding,
 * - Add a new entry for the page while respecting relative order of pages
 * - Ensure correct importFunction is provided which points to the correct chunk
 *   file
 * - If chunk file needs to be created, ensure it's created in src/chunks/
 *   folder When removing,
 * - ensure the correct entry is removed
 * - if the chunk file is no longer needed, remove it from src/chunks/ folder
 */
export const DYNAMIC_IMPORTS = {
  CAMPAIGN_INDEX_PAGE: {
    name: 'CampaignIndexPage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.CampaignIndexPage,
      })),
  },
  CAMPAIGN_EDIT_PAGE: {
    name: 'CampaignEditPage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.CampaignEditPage,
      })),
  },
  MESSAGES_BUILDER_EDITOR_PAGE: {
    name: 'MessagesBuilderEditorPage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.MessagesBuilderEditorPage,
      })),
  },
  BASE_TEMPLATE_BUILDER_EDITOR_PAGE: {
    name: 'BaseTemplateBuilderEditorPage',
    importFunction: () =>
      import('@/chunks/BaseTemplateBuilderEditorPage.chunk').then((module) => ({
        default: module.BaseTemplateBuilderEditorPage,
      })),
  },
  RECURRING_CAMPAIGN_EDIT_PAGE: {
    name: 'RecurringCampaignEditPage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.RecurringCampaignEditPage,
      })),
  },
  RECURRING_MESSAGES_BUILDER_EDITOR_PAGE: {
    name: 'RecurringMessagesBuilderEditorPage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.RecurringMessagesBuilderEditorPage,
      })),
  },
  TRIGGERED_CAMPAIGN_EDIT_PAGE: {
    name: 'TriggeredCampaignEditPage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.TriggeredCampaignEditPage,
      })),
  },
  TRIGGERED_MESSAGES_BUILDER_EDITOR_PAGE: {
    name: 'TriggeredMessagesBuilderEditorPage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.TriggeredMessagesBuilderEditorPage,
      })),
  },
  PROGRAMS_LIST: {
    name: 'ProgramsList',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.ProgramsList,
      })),
  },
  PROGRAM_CREATE_PAGE: {
    name: 'ProgramCreatePage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.ProgramCreatePage,
      })),
  },
  PROGRAM_DETAILS_PAGE: {
    name: 'ProgramDetailsPage',
    importFunction: () =>
      import('@/chunks/CampaignsPage.chunk').then((module) => ({
        default: module.ProgramDetailsPage,
      })),
  },
  CREATIVE_CONTENT_EDITOR_PAGE: {
    name: 'ContentEditor',
    importFunction: () =>
      import('@/chunks/ContentEditor.chunk').then((module) => ({
        default: module.CreativeContentEditorPage,
      })),
  },
  CREATIVE_CONTENT_EDITOR_V2_PAGE: {
    name: 'ContentEditorV2',
    importFunction: () =>
      import('@/chunks/ContentEditorV2.chunk').then((module) => ({
        default: module.CreativeContentEditorV2Page,
      })),
  },
  CONTENT_EDITOR_ABSTRACTION_TEST_PAGE: {
    name: 'ContentEditorAbstractionTest',
    importFunction: () =>
      import('@/chunks/TestContentEditors.chunk').then((module) => ({
        default: module.ContentEditorAbstractionTestPage,
      })),
  },
  RICH_TEXT_EDITOR_TEST_PAGE: {
    name: 'RichTextEditorTest',
    importFunction: () =>
      import('@/chunks/TestContentEditors.chunk').then((module) => ({
        default: module.RichTextEditorTestPage,
      })),
  },
  CONTENT_COMPONENTS_EDITOR_PAGE: {
    name: 'ContentComponentsEditor',
    importFunction: () =>
      import('@/chunks/ContentComponentsEditor.chunk').then((module) => ({
        default: module.ContentComponentsEditorPage,
      })),
  },
  BLOCKS_EDITOR_PAGE: {
    name: 'BlocksEditor',
    importFunction: () =>
      import('@/chunks/BlocksEditor.chunk').then((module) => ({
        default: module.BlocksEditorPage,
      })),
  },
  MOBILE_EDITOR_PAGE: {
    name: 'MobileEditorPage',
    importFunction: () =>
      import('@/chunks/MobileEditor.chunk').then((module) => ({
        default: module.MobileEditorPage,
      })),
  },
  CREATIVE_INDEX_PAGE: {
    name: 'CreativeIndexPage',
    importFunction: () =>
      import('@/chunks/CreativeIndex.chunk').then((module) => ({
        default: module.CreativeIndexPage,
      })),
  },
  CREATIVE_EDIT_PAGE: {
    name: 'CreativeEditPage',
    importFunction: () =>
      import('@/chunks/CreativeEdit.chunk').then((module) => ({
        default: module.CreativeEditPage,
      })),
  },
  AUDIENCES_PAGE: {
    name: 'AudiencesPage',
    importFunction: () =>
      import('@/chunks/AudiencesPage.chunk').then((module) => ({
        default: module.AudiencesPage,
      })),
  },
  AUDIENCE_BUILDER_UNIFIED: {
    name: 'AudienceBuilderUnified',
    importFunction: () =>
      import('@/chunks/AudiencesPage.chunk').then((module) => ({
        default: module.AudienceBuilderUnified,
      })),
  },
  AUDIENCE_BUILDER_PAGE: {
    name: 'AudienceBuilderPage',
    importFunction: () =>
      import('@/chunks/AudiencesPage.chunk').then((module) => ({
        default: module.AudienceBuilderPage,
      })),
  },
  AUDIENCE_DETAIL_PAGE: {
    name: 'AudienceDetailPage',
    importFunction: () =>
      import('@/chunks/AudiencesPage.chunk').then((module) => ({
        default: module.AudienceDetailPage,
      })),
  },

  ANALYTICS_DASHBOARD: {
    name: 'AnalyticsDashboard',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsDashboard,
      })),
  },
  ANALYTICS_CAMPAIGNS_APIMigration: {
    name: 'AnalyticsCampaignsAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsCampaignsAPIMigration,
      })),
  },
  ANALYTICS_CAMPAIGN_DETAILS_API_MIGRATION: {
    name: 'AnalyticsCampaignDetailsAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsCampaignDetailsAPIMigration,
      })),
  },
  ANALYTICS_RECURRING_CAMPAIGN_DETAILS_API_MIGRATION: {
    name: 'AnalyticsRecurringCampaignDetailsAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsRecurringCampaignDetailsAPIMigration,
      })),
  },
  ANALYTICS_CONTENT: {
    name: 'AnalyticsContent',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsContent,
      })),
  },
  ANALYTICS_AUDIENCES: {
    name: 'AnalyticsAudiences',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsAudiences,
      })),
  },
  ANALYTICS_CREATIVES_APIMIGRATION: {
    name: 'AnalyticsCreativesAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsCreativesAPIMigration,
      })),
  },
  ANALYTICS_CREATIVES_V2: {
    name: 'AnalyticsCreativesV2',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsCreativesV2,
      })),
  },
  ANALYTICS_CREATIVE_DETAILS_APIMIGRATION: {
    name: 'AnalyticsCreativeDetailsAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsCreativeDetailsAPIMigration,
      })),
  },
  ANALYTICS_CREATIVE_DETAILS_V2: {
    name: 'AnalyticsCreativeDetailsV2',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsCreativeDetailsV2,
      })),
  },
  ANALYTICS_SUBJECT_LINES_APIMIGRATION: {
    name: 'AnalyticsSubjectLinesAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsSubjectLinesAPIMigration,
      })),
  },
  ANALYTICS_SEGMENTS_APIMIGRATION: {
    name: 'AnalyticsSegmentsAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsSegmentsAPIMigration,
      })),
  },
  ANALYTICS_SEGMENTS_CREATE: {
    name: 'AnalyticsSegmentsCreate',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsSegmentsCreate,
      })),
  },
  ANALYTICS_EXPERIMENTS_APIMIGRATION: {
    name: 'AnalyticsExperimentsAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsExperimentsAPIMigration,
      })),
  },
  ANALYTICS_EXPERIMENT_DETAILS_APIMIGRATION: {
    name: 'AnalyticsExperimentDetailsAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsExperimentDetailsAPIMigration,
      })),
  },
  ANALYTICS_SEGMENT_DETAILS_APIMIGRATION: {
    name: 'AnalyticsSegmentDetailsAPIMigration',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsSegmentDetailsAPIMigration,
      })),
  },
  ANALYTICS_PROGRAMS: {
    name: 'AnalyticsPrograms',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsPrograms,
      })),
  },
  ANALYTICS_PROGRAM_DETAILS: {
    name: 'AnalyticsProgramDetails',
    importFunction: () =>
      import('@/chunks/AnalyticsPage.chunk').then((module) => ({
        default: module.AnalyticsProgramDetails,
      })),
  },
  DATA_CONNECTIONS_PAGE: {
    name: 'DataConnectionsPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.DataConnectionsPage,
      })),
  },
  DATA_CONNECTIONS_CREATE_PAGE: {
    name: 'DataConnectionsCreatePage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.DataConnectionsCreatePage,
      })),
  },
  DATA_CONNECTIONS_DETAIL_PAGE: {
    name: 'DataConnectionsDetailPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.DataConnectionsDetailPage,
      })),
  },
  TABLES_PAGE: {
    name: 'TablesPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.TablesPage,
      })),
  },
  TABLES_CREATE_AND_EDIT_PAGE: {
    name: 'TablesCreateAndEditPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.TablesCreateAndEditPage,
      })),
  },
  JOBS_PAGE: {
    name: 'JobsPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.JobsPage,
      })),
  },
  JOB_DETAIL_PAGE: {
    name: 'JobDetailPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.JobDetailPage,
      })),
  },
  JOB_EXPORT_DETAIL_PAGE: {
    name: 'JobExportDetailPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.JobExportDetailPage,
      })),
  },
  JOB_CREATE_PAGE: {
    name: 'JobCreatePage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.JobCreatePage,
      })),
  },
  DATASETS_PAGE: {
    name: 'DatasetsPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.DatasetsPage,
      })),
  },
  DATASET_CREATE_PAGE: {
    name: 'DatasetCreatePage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.DatasetCreatePage,
      })),
  },
  DATASET_DETAIL_PAGE: {
    name: 'DatasetDetailPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.DatasetDetailPage,
      })),
  },
  DATASET_SOURCE_PAGE: {
    name: 'DatasetSourcePage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.DatasetSourcePage,
      })),
  },
  DATASET_JOB_DETAIL_PAGE: {
    name: 'DatasetJobDetailPage',
    importFunction: () =>
      import('@/chunks/DataPage.chunk').then((module) => ({
        default: module.DatasetJobDetailPage,
      })),
  },
  SETTINGS_PAGE: {
    name: 'SettingsPage',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.SettingsPage,
      })),
  },
  ORGANIZATIONS: {
    name: 'Organizations',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.Organizations,
      })),
  },
  USERS_SETTINGS: {
    name: 'UsersSettings',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.UsersSettings,
      })),
  },
  USER_EDIT: {
    name: 'UserEdit',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.UserEdit,
      })),
  },
  ACCOUNTS_EDIT: {
    name: 'AccountsEdit',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.AccountsEdit,
      })),
  },
  ACCOUNTS_ROLES_EDIT: {
    name: 'AccountsRolesEdit',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.AccountsRolesEdit,
      })),
  },
  CREATE_ROLE_PAGE: {
    name: 'CreateRolePage',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.CreateRolePage,
      })),
  },
  APPLICATION_EDIT_PAGE: {
    name: 'ApplicationEditPage',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.ApplicationEditPage,
      })),
  },
  NOTIFICATION_SETTINGS: {
    name: 'NotificationSettings',
    importFunction: () =>
      import('@/chunks/SettingsPage.chunk').then((module) => ({
        default: module.NotificationSettings,
      })),
  },
  GRAPHIQL_COMPONENT: {
    name: 'GraphiQLComponent',
    importFunction: () =>
      import('@/chunks/GraphiQLPage.chunk').then((module) => ({
        default: module.GraphiQLComponent,
      })),
  },
  JOURNEY_PAGE: {
    name: 'JourneyPage',
    importFunction: () =>
      import('@/chunks/JourneyPage.chunk').then((module) => ({
        default: module.JourneyPage,
      })),
  },
  INK_AI_PAGE: {
    name: 'InkAiPage',
    importFunction: () =>
      import('@/chunks/InkAiPage.chunk').then((module) => ({
        default: module.InkAiPage,
      })),
  },
  BRAND_KIT_PAGE: {
    name: 'BrandKitPage',
    importFunction: () =>
      import('@/chunks/BrandKitPage.chunk').then((module) => ({
        default: module.BrandKitPage,
      })),
  },
  BRAND_KIT_DETAILS_PAGE: {
    name: 'BrandKitDetailsPage',
    importFunction: () =>
      import('@/chunks/BrandKitPage.chunk').then((module) => ({
        default: module.BrandKitDetailsPage,
      })),
  },
};

const errorHandlerWrapper = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importFunction: () => Promise<{ default: React.ComponentType<any> }>,
) => {
  return async () => {
    try {
      return await importFunction();
    } catch (error) {
      console.error('Failed to load chunk: ', error);
      // this catches any error that happens during the dynamic import. however,
      // to prevent this error from triggering the "Oops" page, assign it a
      // unique name
      const chunkLoadError = new Error(
        'Error loading dynamically imported module',
      );
      chunkLoadError.name = 'ChunkLoadError';
      throw chunkLoadError;
    }
  };
};

// since this is dynamically importing different components during runtime, it
// is not possible to know the prop types statically, hence the usage of "any"
export const chunkMapping: Record<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  () => Promise<{ default: React.ComponentType<any> }>
> = Object.values(DYNAMIC_IMPORTS).reduce(
  (acc, { name, importFunction }) => {
    acc[name] = errorHandlerWrapper(importFunction);
    return acc;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {} as Record<string, () => Promise<{ default: React.ComponentType<any> }>>,
);
